import { APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { CdkScrollable, OverlayModule } from '@angular/cdk/overlay';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';

import { provideEnvironmentNgxMask } from 'ngx-mask';
import { NgxsModule } from '@ngxs/store';

import { NgbModule } from '@ng-bootstrap/ng-bootstrap';

import { MAT_EXPANSION_PANEL_DEFAULT_OPTIONS } from '@angular/material/expansion';
import { MatMenuModule } from '@angular/material/menu';

import * as Sentry from '@sentry/angular';

// MSAL
import {
    MsalModule,
    MsalGuard,
    MSAL_INSTANCE,
    MSAL_GUARD_CONFIG,
    MsalService,
    MsalRedirectComponent,
} from '@azure/msal-angular';
import {
    PublicClientApplication,
    BrowserCacheLocation,
    InteractionType,
} from '@azure/msal-browser';
import { initializeAppFactory, MSAL_BOOTSTRAP_PROVIDER } from './app.initializer';

import {
    AlertModule,
    DropdownMenuModule,
    TextfieldModule,
    LayerModule,
    LayerComponent,
} from '@ed---interne/ng-uui-components';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';

import { CoreModule } from './core/core.module';
import { AuthState } from './core/states/auth.state';

import { SharedModule } from './shared/shared.module';

import { UsersState } from './features/admin/users/users.state';
import { DocumentsState } from './shared/states/documents.state';
import { AuthService } from './core/services/auth.service';
import { UsersPaginationState } from './features/admin/users/users-pagination.state';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { OrganizationState } from './features/admin/organization/organization.state';
import { environment } from '../environments/environment';
import { MainModule } from './features/main/main.module';
import { AdminModule } from './features/admin/admin.module';
import { AuthModule } from './features/auth/auth.module';

export const AZURE_CLIENT_ID = environment.azureClientId;
const AZURE_TENTANT_ID = environment.azureTenantId;

// === Factories MSAL (déjà vues précédemment)
export function MSALInstanceFactory() {
    return new PublicClientApplication({
        auth: {
            clientId: AZURE_CLIENT_ID,
            authority: `https://login.microsoftonline.com/${AZURE_TENTANT_ID}`,
            redirectUri: window.location.origin + '/azure-callback',
            postLogoutRedirectUri: window.location.origin,
            navigateToLoginRequestUrl: false,
        },
        cache: { cacheLocation: BrowserCacheLocation.LocalStorage },
    });
}

export function MSALGuardConfigFactory() {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: { scopes: ['openid', 'profile', 'email'] },
    };
}

@NgModule({
    declarations: [AppComponent],
    imports: [
        BrowserModule,
        MsalModule,
        CoreModule.forRoot(),
        SharedModule,
        AlertModule,
        AppRoutingModule,
        MainModule,
        AdminModule,
        AuthModule,
        LayerModule,
        NgxsModule.forRoot([
            AuthState,
            UsersState,
            DocumentsState,
            UsersPaginationState,
            OrganizationState,
        ]),
    ],
    providers: [
        { provide: MSAL_INSTANCE, useFactory: MSALInstanceFactory },
        { provide: MSAL_GUARD_CONFIG, useFactory: MSALGuardConfigFactory },
        MsalGuard,
        MsalService,
        MSAL_BOOTSTRAP_PROVIDER,
        {
            provide: APP_INITIALIZER,
            useFactory: initializeAppFactory,
            deps: [AuthService, Sentry.TraceService],
            multi: true,
        },
        {
            provide: ErrorHandler,
            useValue: Sentry.createErrorHandler({
                showDialog: true,
            }),
        },
        {
            provide: Sentry.TraceService,
            deps: [Router],
        },
        {
            provide: MAT_EXPANSION_PANEL_DEFAULT_OPTIONS,
            useValue: {
                expandedHeight: '28px',
                collapsedHeight: '28px',
            },
        },
        provideEnvironmentNgxMask(),
    ],
    bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}
